/* eslint-disable multiline-ternary */
/* eslint-disable camelcase */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { GridExpandMoreIcon, useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RocketLaunch } from '@mui/icons-material';
import { isMobile } from 'react-device-detect';
import styles from './styles';
import Datagrid from '../../ui/Datagrid';
import columns from './table_config';
import SelectEditInputTextField from '../../ui/SelectEditInputTextField';
import { CONTROL_TABLE_PARAMETER_MAP, HYDRAULICS_MODEL, HYDRAULICS_MODEL_VALUES, HYDRAULICS_RUN_DETAILS, HYDRAULICS_RUN_TYPES, TOOLTIP_COLORS } from '../../../utils/constants';
import WellboreRunInputs from './WellboreRunInputs';
import CuttingRunInputs from './CuttingsRunInputs';
import StrippingInputs from './StrippingRunInputs';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import { runFeaturedWellHydraulics } from '../../../store/actions/hydraulicsLanding';
import { REDUX_ACTIONS } from '../../../shared/config/constants';
import RolloverRunInputs from './RolloverRunInputs';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import ParametricAnalysisRunInputs from './ParametricAnalysisRunInputs';
import DisplacementInputs from './DisplacementRunInputs';
import MobileHydraulicsRunInput from '../../mobile/hydraulics/featuredWellRunInputs/hydraulicsRunInput';
import SelectEditValueTypes from '../../ui/SelectEditValueTypes';
import MobileSelectEditValueTypes from '../../mobile/hydraulics/SelectEditValueTypes';
import MobileSelectEditInputTextField from '../../mobile/hydraulics/SelectEditInputTextField';
import { COLORS } from '../../../shared/config/colors';
import MudPumpRunInputsModel from './RolloverRunInputs/MudPumpRunInputsModal';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Premium from '../../../assets/images/PRO logo PMS green only.svg';
function FeaturedWellRunInputs({
  hydraulicsLanding,
  featuredWellRun,
  chartType,
  handleChartTypeClick,
  handleChange
}) {
  const dispatch = useDispatch();
  const classes = styles();
  const apiRef = useGridApiRef();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(true);

  const [properties, setProperties] = useState([]);
  const [hydraulicParameterData, setHydraulicsParameterData] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});

  const [wellbore, setWellbore] = useState({
    pipe: [],
    annular: []
  });
  const [checkWellbore, setCheckWellbore] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState('None');
  const [targetECD, setTargetECD] = useState('');
  const [targetData, setTargetData] = useState({});

  const [cuttingData, setCuttingData] = useState({});
  const [updateCuttingData, setUpdateCuttingData] = useState({});
  const [invalidCuttingRows, setInvalidCuttingRows] = useState({});

  const [displacementData, setDisplacementData] = useState([]);
  const [rolloverData, setRolloverData] = useState(featuredWellRun?.inputs);
  const [mudPumpData, setMudPumpData] = useState(hydraulicsLanding?.data?.mudPumps);
  const [strippingData, setStrippingData] = useState([]);
  const WELLBORE_DRILL_FLUID = { fluid_id: 'air_filling_mud', name: 'Air', MD: 0 };
  const [calculatedFlowRate, setCalculatedFlowRate] = useState('');
  const [showMudPumps, setShowMudPumps] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hydraulicsRunUnits = hydraulicsLanding?.units?.hydraulicsRun;

  useEffect(() => {
    if (featuredWellRun?.inputs && hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.STRIPPING?.typeKey) {
      setStrippingData(featuredWellRun.inputs);
    }
  }, [featuredWellRun]);

  const [parametricAnalysisData, setParametricAnalysisData] = useState(featuredWellRun?.inputs);

  const inputPayloadMap = {
    displacement: displacementData.map(({ _id, ...rest }) => rest),
    rollover: {
      fromFluidId: rolloverData.fromFluidId,
      toFluidId: rolloverData.toFluidId,
      pumps_output: Number(rolloverData?.pumps_output),
      pumps_spm: Number(rolloverData?.pumps_spm),
    },
    stripping: strippingData,
    parametricAnalysis: parametricAnalysisData
    // add for other run types
  };

  const {
    unSavedExists,
  } = useUnsavedChanges(properties);

  useEffect(() => {
    if (unSavedExists) {
      dispatch({
        type: REDUX_ACTIONS.HYDRAULICS_LANDING_RUN_INPUTS_CHANGED,
      });
      setTimeout(() => {
        setTooltipIsOpen(true);
      }, 500);
    }
  }, [unSavedExists]);

  const hydraulicsDefaults = hydraulicsLanding?.data?.[HYDRAULICS_RUN_TYPES?.HYDRAULICS?.typeKey]?.run_details;

  const getHydraulicsFallback = (value, fallback) => {
    if (
      value === null ||
      value === undefined ||
      value === 0
    ) {
      return fallback;
    }

    return value;
  };

  useEffect(() => {
    const updatedRunDetails = [featuredWellRun?.run_details].map(detail => {
      const originalDrillFluid = hydraulicsLanding.data.drillFluids.find(item => item.id === detail.drillFluidsId);
      const drillFluid = originalDrillFluid || hydraulicsLanding.data.drillFluids[0];
      const HYDRAULICS_MODEL_LABELS = Object.fromEntries(
        Object.entries(HYDRAULICS_MODEL_VALUES).map(([k, v]) => [v, k])
      );
      const displayRheoModel =
        detail?.rheo_model
          ? HYDRAULICS_MODEL_LABELS[detail.rheo_model] || detail.rheo_model
          : 'API-RP-YPL (2003)';

      const holeDepth = getHydraulicsFallback(
        detail?.hole_depth,
        hydraulicsDefaults?.hole_depth
      );

      const bitDepthOffset = getHydraulicsFallback(
        detail?.bit_depth_offset,
        hydraulicsDefaults?.bit_depth_offset
      );
      const updatedDetail = {
        ...detail,
        drillStringsId: `Drillstring @ ${holeDepth.toFixed(hydraulicsLanding?.units?.drillStrings?.MD.decimals)}${hydraulicsLanding?.units?.drillStrings?.MD.label}`,
        drillFluidsId: drillFluid
          ? `${drillFluid.name.trim()} @ ${drillFluid.MD.toFixed(hydraulicsLanding?.units?.drillFluids?.MD.decimals)}${hydraulicsLanding?.units?.drillFluids?.MD.label}`
          : '',
        bit_depth: getHydraulicsFallback(
          detail?.bit_depth,
          hydraulicsDefaults?.bit_depth ?? (holeDepth + bitDepthOffset)
        ),
        rpm: getHydraulicsFallback(
          detail?.rpm,
          hydraulicsDefaults?.rpm ?? 0.0
        ),
        correction_factor: detail?.correction_factor ?? 1.0,
        eq_losses: detail?.eq_losses ?? 0.0,
        target_data: detail?.target_data || 'None',
        well_init: detail?.well_init || {},
        rheo_model: displayRheoModel,
        hole_depth: holeDepth,
        rop: getHydraulicsFallback(
          detail?.rop,
          hydraulicsDefaults?.rop
        ),
        flow_in: getHydraulicsFallback(
          detail?.flow_in,
          hydraulicsDefaults?.flow_in
        ),
        sbp: getHydraulicsFallback(
          detail?.sbp,
          hydraulicsDefaults?.sbp
        ),
      };

      return updatedDetail;
    });
    setProperties(updatedRunDetails);
  }, [hydraulicsLanding.data, featuredWellRun]);

  useEffect(() => {
    if (hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.ROLLOVER?.typeKey && properties.length) {
      const rollover = featuredWellRun?.inputs;
      rollover.fromFluidId = hydraulicsLanding?.data?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(hydraulicsLanding?.units?.drillFluids?.MD.decimals)}${hydraulicsLanding?.units?.drillFluids.MD.label}` === properties[0]?.drillFluidsId)[0].fluid_id;
      setRolloverData(rollover);
    }
  }, [properties]);

  useEffect(() => {
    const mudpumpsData = mudPumpData || [];
    const hasMudpumps = mudpumpsData.length > 0;

    const pumps_output = hasMudpumps
      ? Number(mudpumpsData[0]?.bbl_stk || 0)
      : '';

    const pumps_spm = hasMudpumps
      ? mudpumpsData.reduce((sum, pump) => {
        const spm = Number(pump?.spm ?? 0);
        return sum + (Number.isFinite(spm) ? spm : 0);
      }, 0)
      : '';

    const pumps = rolloverData?.pumps_output ? rolloverData?.pumps_output : pumps_output;
    const spm = rolloverData?.pumps_spm ? rolloverData?.pumps_spm : pumps_spm;
    const multiplier = 42;
    const unitLabel = 'gpm';

    if (typeof pumps === 'number' && typeof spm === 'number') {
      const result = pumps * spm * multiplier;
      setCalculatedFlowRate(`${result.toFixed(1)}${unitLabel}`);
    } else {
      setCalculatedFlowRate('');
    }
  }, [rolloverData, mudPumpData]);

  useEffect(() => {
    const targetData = featuredWellRun?.run_details?.target_data;
    const decimals = 2;
    setTargetData(targetData);
    setAnchorPoint(targetData?.anchor_point || 'None');
    setCuttingData(featuredWellRun?.run_details?.cuttings_data);
    setUpdateCuttingData(featuredWellRun?.run_details?.cuttings_data);
    const convertedECD = parseFloat(targetData?.target_ecd);
    setTargetECD(Number(convertedECD) ? convertedECD.toFixed(decimals) : '');
    let hasDeletedWellInitFluid = false;
    if (featuredWellRun?.run_details?.well_init) {
      const { drillFluids: originalDrillFLuids = [] } = hydraulicsLanding?.data || {};
      const drillFluids = [...originalDrillFLuids, WELLBORE_DRILL_FLUID];
      const units = { decimals: 2, label: 'ft' };
      const formatFluid = (id) => {
        const fluid = drillFluids.find((item) => item.fluid_id === id);
        return fluid
          ? `${fluid.name.trim()} @ ${fluid.MD.toFixed(units.decimals)}${units.label}`
          : '';
      };

      const transformWellInitData = (data) =>
        data?.map((detail) => {
          const fluidExists = drillFluids.some(fluid => fluid.fluid_id === detail.fluid_id);

          if (!fluidExists) {
            hasDeletedWellInitFluid = true;
          }

          return {
            ...detail,
            from: Number(detail.from),
            to: Number(detail.to),
            fluid_id: formatFluid(detail.fluid_id),
          };
        });

      const transformedPipe = featuredWellRun?.run_details?.well_init?.pipe ? transformWellInitData(featuredWellRun?.run_details?.well_init?.pipe) : [];
      const transformedAnnular = featuredWellRun?.run_details?.well_init?.annular ? transformWellInitData(featuredWellRun?.run_details?.well_init?.annular) : [];

      setWellbore({
        pipe: transformedPipe,
        annular: transformedAnnular,
      });

      if (hasDeletedWellInitFluid) {
        setCheckWellbore(true);
        dispatch(
          enqueueSnackbar(
            'Configured Drillfluid in Wellbore Initialization is not found, Please Check',
            'error',
            new Date().getTime() + Math.random()
          )
        );
      } else { setCheckWellbore(false); }
    } else {
      setWellbore({
        pipe: [],
        annular: []
      });
    }
  }, [featuredWellRun]);

  useEffect(() => {
    if (hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.PARAMETRIC_ANALYSIS?.typeKey) {
      setParametricAnalysisData(featuredWellRun?.inputs);
    }
  }, [featuredWellRun]);

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const updatedItem = {
            ...item,
            [field]: value,
            isDirty: value !== item[field],
          };

          if (
            field === 'hole_depth' &&
            (item.bit_depth === '' || item.bit_depth === null || item.bit_depth === undefined || item.bit_depth === item.hole_depth)
          ) {
            updatedItem.bit_depth = value;
          }

          return updatedItem;
        }
        return item;
      });

      return updatedProperties;
    });
  };

  const checkHydraulicsValues = (row) => {
    const invalidFields = [];
    const cuttingInvalidFields = [];
    const isInvalidNumber = (val) => isNaN(val) || val === '' || (typeof val === 'string' && val.trim() === '');

    if (row?.hole_depth === undefined || row?.hole_depth === null || isInvalidNumber(row.hole_depth)) {
      invalidFields.push('HOLE_DEPTH');
    }
    if (row?.bit_depth === undefined || row?.bit_depth === null || isInvalidNumber(row.bit_depth)) {
      invalidFields.push('BIT_DEPTH');
    }
    if (row?.rop === undefined || row?.rop === null || isInvalidNumber(row.rop)) {
      invalidFields.push('ROP');
    }
    if (Number(row?.flow_in) > 0 && (row?.eq_losses === undefined || row?.eq_losses === null || isInvalidNumber(row.eq_losses))) {
      invalidFields.push('Equipment Losses');
    }
    if (row?.flow_in === undefined || row?.flow_in === null || isInvalidNumber(row.flow_in)) {
      invalidFields.push('FLOW_RATE');
    }
    if (row?.sbp === undefined || row?.sbp === null || isInvalidNumber(row.sbp)) {
      invalidFields.push('SBP');
    }
    if (row?.rpm === undefined || row?.rpm === null || isInvalidNumber(row.rpm)) {
      invalidFields.push('RPM');
    }
    if (row?.correction_factor === undefined || row?.correction_factor === null || isInvalidNumber(row.correction_factor)) {
      invalidFields.push('Correction Factor');
    }

    // Mark invalid hydraulics row if any field is invalid
    if (invalidFields.length > 0) {
      setInvalidRows((prev) => ({ ...prev, [row._id]: true }));
    } else {
      setInvalidRows((prev) => ({ ...prev, [row._id]: false }));
    }

    // Check cutting data only if ROP > 0
    if (row.rop > 0) {
      if (updateCuttingData?.diameter === undefined || updateCuttingData?.diameter === null || isInvalidNumber(updateCuttingData.diameter)) {
        cuttingInvalidFields.push('Diameter');
      }
      if (updateCuttingData?.thickness === undefined || updateCuttingData?.thickness === null || isInvalidNumber(updateCuttingData.thickness)) {
        cuttingInvalidFields.push('Thickness');
      }
      if (updateCuttingData?.density === undefined || updateCuttingData?.density === null || isInvalidNumber(updateCuttingData.density)) {
        cuttingInvalidFields.push('Density');
      }
      if (updateCuttingData?.porosity === undefined || updateCuttingData?.porosity === null || isInvalidNumber(updateCuttingData.porosity)) {
        cuttingInvalidFields.push('Porosity');
      }

      if (cuttingInvalidFields.length > 0) {
        setInvalidCuttingRows((prev) => ({ ...prev, [updateCuttingData._id]: true }));
        dispatch(
          enqueueSnackbar(
            `Please enter valid values for: ${cuttingInvalidFields.join(', ')}`,
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      } else {
        setInvalidCuttingRows((prev) => ({ ...prev, [updateCuttingData._id]: false }));
      }
    }

    const showError = (message, rowId) => {
      dispatch(enqueueSnackbar(message, 'error'));
      setInvalidRows(prev => ({ ...prev, [rowId]: true }));
      return false;
    };

    const showCuttingError = (message, rowId) => {
      dispatch(enqueueSnackbar(message, 'error'));
      setInvalidCuttingRows(prev => ({ ...prev, [rowId]: true }));
      return false;
    };

    const validateRunDetails = (runDetails) => {
      const { bit_depth, flow_in, hole_depth, sbp, rop, _id, rpm, correction_factor, eq_losses } = runDetails;
      const { thickness, porosity, diameter, density, _id: cuttingId } = updateCuttingData;
      const cuttingData = hydraulicsRunUnits?.inputs?.cuttings_data;
      const minDiameter = cuttingData?.diameter?.min;
      const maxDiameter = cuttingData?.diameter?.max;
      const minThickness = cuttingData?.thickness?.min;
      const maxThickness = cuttingData?.thickness?.max;
      const minDensity = cuttingData?.density?.min;
      const maxDensity = cuttingData?.density?.max;
      const minPorosity = cuttingData?.porosity?.min;
      const maxPorosity = cuttingData?.porosity?.max;
      const min_hole_depth = hydraulicsRunUnits.inputs.hole_depth.min;
      const min_rop = hydraulicsRunUnits.inputs.rop.min;
      const max_rop = hydraulicsRunUnits.inputs.rop.max;
      const min_sbp = hydraulicsRunUnits.inputs.sbp.min;
      const min_flow_in = hydraulicsRunUnits.inputs.flow_in.min;
      const min_rpm = hydraulicsRunUnits.inputs.rpm.min;
      const max_rpm = hydraulicsRunUnits.inputs.rpm.max;
      const min_correction_factor = 0.25;
      const max_correction_factor = 3;
      const min_eqLosses = hydraulicsRunUnits.inputs.eq_losses.min;
      const max_eqLosses = hydraulicsRunUnits.inputs.eq_losses.max;

      if (Number(hole_depth) < min_hole_depth) return showError(HYDRAULICS_RUN_DETAILS.HOLE_DEPTH, _id);
      if (Number(bit_depth) < 0 || Number(bit_depth) > Number(hole_depth)) return showError(`Bit Depth should be in range 0 to Hole Depth (${hole_depth})`, _id);
      if (Number(rop) < min_rop || Number(rop) > max_rop) return showError(`ROP should be between ${min_rop} and ${max_rop}`, _id);
      if (Number(sbp) < min_sbp) return showError(HYDRAULICS_RUN_DETAILS.SBP, _id);
      if (Number(correction_factor) < min_correction_factor || Number(correction_factor) > max_correction_factor) return showError(`Correction Factor should be between ${min_correction_factor} and ${max_correction_factor}`, _id);
      if (Number(flow_in) < min_flow_in) return showError(HYDRAULICS_RUN_DETAILS.FLOW_IN, _id);
      if (Number(rpm) < min_rpm || Number(rpm) > max_rpm) return showError(`RPM should be between ${min_rpm} and ${max_rpm}`, _id);
      if (Number(flow_in) > 0) {
        if (Number(eq_losses) < min_eqLosses || Number(eq_losses) > max_eqLosses) return showError(`Equipment Losses should be between ${min_eqLosses} and ${max_eqLosses}`, _id);
      }
      if (Number(rop) > 0) {
        if (Number(diameter) < minDiameter || Number(diameter) > maxDiameter) return showCuttingError(`Diameter should be between ${minDiameter} and ${maxDiameter} ${cuttingData.diameter.label}`, cuttingId);
        if (Number(thickness) < minThickness || Number(thickness) > maxThickness) return showCuttingError(`Thickness should be between ${minThickness} and ${maxThickness} ${cuttingData.thickness.label}`, cuttingId);
        if (Number(density) < minDensity || Number(density) > maxDensity) return showCuttingError(`Density should be between ${minDensity} and ${maxDensity} ${cuttingData.density.label}`, cuttingId);
        if (Number(porosity) < minPorosity || Number(porosity) > maxPorosity) return showCuttingError(`Porosity should be between ${minPorosity} and ${maxPorosity} ${cuttingData.porosity.label}`, cuttingId);
      }
      return true;
    };

    // Final decision based on hydraulics validation
    if (invalidFields.length === 0 && cuttingInvalidFields.length === 0) {
      return validateRunDetails(row);
    } else {
      dispatch(
        enqueueSnackbar(
          `Please enter valid values for: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const transformWellbore = (wellbore, hydraulics) => {
    if (!hydraulics?.drillFluids) return wellbore;

    const { drillFluids: originalDrillFLuids = [] } = hydraulics || {};
    const units = hydraulicsLanding?.units?.drillFluids?.MD || { decimals: 2, label: 'ft' };
    const drillFluids = [...originalDrillFLuids, WELLBORE_DRILL_FLUID];

    const fluidMap = drillFluids.reduce((map, item) => {
      const key = `${item?.name?.trim()} @ ${item?.MD.toFixed(units.decimals)}${units.label}`;
      map[key] = item?.fluid_id;
      return map;
    }, {});

    const mapWellInitData = (data) =>
      data.map((row) => ({
        from: Number(row.from),
        to: Number(row.to),
        fluid_id: fluidMap[row.fluid_id] || null,
      }));

    return {
      pipe: mapWellInitData(wellbore.pipe),
      annular: mapWellInitData(wellbore.annular),
    };
  };

  const transformRunsECDSpan = (targetData) => {
    if (!targetData.ecd_span) return [];
    return targetData?.ecd_span?.map((row) => ({
      name: row?.name,
      low_emw: Number(row.low_emw),
      high_emw: Number(row.high_emw),
    }));
  };

  const transformControlsTable = (targetData) => {
    if (!targetData.controls_table) return [];
    return targetData?.controls_table?.map((row) => {
      const parameter = CONTROL_TABLE_PARAMETER_MAP[row?.parameter] || row?.parameter;
      return ({
        maintain: row?.parameter === 'SBP' ? '-' : row?.maintain?.toLowerCase(),
        parameter,
        from: Number(row.from),
        to: Number(row.to),
        step_size: row?.parameter === 'SBP' ? '-' : Number(row.step_size),
      });
    });
  };

  const saveProperty = async (row) => {
    if (checkHydraulicsValues(row)) {
      const validationError = '';
      if (validationError === '') {
        const converted_hole_depth = Number(row.hole_depth);

        const converted_bit_depth = Number(row.bit_depth);

        const converted_bit_depth_offset = converted_bit_depth - converted_hole_depth;

        const converted_rop = Number(row.rop);
        const converted_eq_losses = Number(row.eq_losses);
        const converted_flow_in = Number(row.flow_in);
        const converted_spb = Number(row.sbp);
        const cuttingsBody = {
          diameter: Number(updateCuttingData.diameter),
          thickness: Number(updateCuttingData.thickness),
          density: Number(updateCuttingData.density),
          porosity: Number(updateCuttingData.porosity),
        };
        const updatedWellbore = transformWellbore(wellbore, hydraulicsLanding.data);
        const updatedRunsECDSpan = transformRunsECDSpan(targetData);
        const updatedControlsTable = transformControlsTable(targetData);
        const body = {
          hole_depth: converted_hole_depth,
          bit_depth: converted_bit_depth,
          bit_depth_offset: converted_bit_depth_offset,
          rop: converted_rop,
          eq_losses: converted_flow_in > 0 ? converted_eq_losses : 0,
          flow_in: converted_flow_in,
          sbp: converted_spb > 0 ? converted_spb : 0.0,
          correction_factor: Number(row.correction_factor),
          rpm: Number(row.rpm),
          drillStringsId: featuredWellRun?.run_details?.drillStringsId,
          drillFluidsId: hydraulicsLanding?.data?.drillFluids.filter((item) => `${item?.name?.trim()} @ ${item?.MD.toFixed(hydraulicsLanding?.units?.drillFluids?.MD.decimals)}${hydraulicsLanding?.units?.drillFluids?.MD.label}` === row?.drillFluidsId)[0].id,
          cuttings_data: cuttingsBody,
          rheo_model: HYDRAULICS_MODEL_VALUES[row?.rheo_model],
          target_data: (hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.STRIPPING?.typeKey || hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.DISPLACEMENT?.typeKey) || (Object.keys(targetData).length === 0)
            ? {}
            : {
                anchor_point: targetData.anchor_point,
                target_ecd: Number(targetData.target_ecd),
                ecd_span: updatedRunsECDSpan,
                controls_table: updatedControlsTable
              },
          well_init: updatedWellbore,
          _id: featuredWellRun?.runId
        };

        const payload = {
          runId: featuredWellRun?.runId,
          data: {
            type: hydraulicsLanding.runType,
            run_details: body,
            inputs: inputPayloadMap[hydraulicsLanding.runType]
          }
        };
        if (hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.ROLLOVER?.typeKey) {
          const { inputs } = payload.data;
          const units = hydraulicsLanding?.units?.rollover;
          const { fromFluidId, toFluidId, pumps_output, pumps_spm } = inputs || {};
          if (fromFluidId && fromFluidId === toFluidId) {
            dispatch(
              enqueueSnackbar(
                'From fluid and To fluid cannot be the same',
                'error',
                new Date().getTime() + Math.random()
              )
            );
            return;
          }
          const min_pumps_output = units?.pumps_output?.min;
          const max_pumps_output = units?.pumps_output?.max;
          const min_pumps_spm = units?.pumps_spm?.min;
          const max_pumps_spm = units?.pumps_spm?.max;
          if (pumps_output !== '' && !isNaN(pumps_output)) {
            const value = Number(pumps_output);
            if (value <= min_pumps_output || value > max_pumps_output) {
              dispatch(
                enqueueSnackbar(
                  `Pumps Output should be greater than ${min_pumps_output} and less than ${max_pumps_output}`,
                  'error',
                  new Date().getTime() + Math.random()
                )
              );
              return;
            }
          }
          if (pumps_spm !== '' && !isNaN(pumps_spm)) {
            const value = Number(pumps_spm);
            if (value <= min_pumps_spm || value > max_pumps_spm) {
              dispatch(
                enqueueSnackbar(
                  `Pumps SPM should be greater than ${min_pumps_spm} and less than ${max_pumps_spm}`,
                  'error',
                  new Date().getTime() + Math.random()
                )
              );
              return;
            }
          }
        }
        dispatch(runFeaturedWellHydraulics(payload));

        if (!isMobile) {
          setTimeout(() => {
            Object.keys(row).forEach((field) => {
              apiRef.current.setCellMode(row._id, field, 'view');
            });
          }, 100);
        }
      } else {
        if (validationError.length) {
          setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        }
        dispatch(
          enqueueSnackbar(
            validationError,
            'error',
            new Date().getTime() + Math.random()
          )
        );
      }
    }
  };

  const renderValueCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} loading={hydraulicsLanding?.hydraulicsRunLoading} />
      );
    }
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderModelCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditValueTypes
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={HYDRAULICS_MODEL}
        // loading={proxyLoader}
        />
      );
    }
    return (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={HYDRAULICS_MODEL}
      />
    );
  };

  const renderSubmitButton = () => (
    <Tooltip
      title="Try Me!"
      arrow
      placement='left'
      open={tooltipIsOpen || hydraulicsLanding?.hideChartResults}
      onMouseEnter={() => setTooltipIsOpen(false)}
      onOpen={() => setTooltipIsOpen(false)}
      onClose={() => setTooltipIsOpen(false)}
      PopperProps={{
        style: { zIndex: 0, width: '72px' },
        disablePortal: true,
        popperOptions: {
          positionFixed: true,
        }
      }}

      componentsProps={{
        tooltip: {
          sx: (tooltipIsOpen || hydraulicsLanding?.hideChartResults) && {
            '@keyframes tryme': {
              '0%': {
                transform: 'scale(1)'
              },
              '5%': {
                transform: 'scale(0.7)'
              },
              '10%': {
                transform: 'scale(1.2)'
              },
              '20%': {
                transform: 'scale(1.1)'
              },
              '30%': {
                transform: 'scale(1)'
              },
            },
            animation: 'tryme 2.5s ease-in-out infinite',
            backgroundColor: TOOLTIP_COLORS.background,
            fontWeight: 'bold',
            fontSize: '12px',
            color: TOOLTIP_COLORS.color,
            '& .MuiTooltip-arrow': {
              color: TOOLTIP_COLORS.background,
            },
          }
        }
      }}
    >
      <IconButton
        variant="solid"
        onClick={() => {
          setTimeout(() => {
            saveProperty(properties[0]);
          }, 100);
        }}
      >
        <RocketLaunch
          sx={{
            fontSize: {
              xs: '1.5rem',
              md: '2rem',
            },
            mr: 0.1,
            ml: -2.1
          }}
        />
      </IconButton>
    </Tooltip>
  );

  const renderMobileRunTypeSelector = () => (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Box display="flex" alignItems="center">
          <IconButton
            onClick={() => setDrawerOpen(true)}
            disabled={hydraulicsLanding?.hydraulicsRunLoading}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h5"
            sx={{
              ml: 1.2,
            }}
          >
            {chartType?.name}
          </Typography>
        </Box>

        {
          !hydraulicsLanding?.hydraulicsRunLoading &&
          renderSubmitButton()
        }
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box className={classes.mobileDrawer}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
          >
            <Box
              component="img"
              alt="Premium"
              src={Premium}
              sx={{
                width: '120px',
                height: 'auto',
              }}
            />

            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {Object.values(HYDRAULICS_RUN_TYPES).map((item) => (
              <ListItemButton
                key={item.typeKey}
                selected={item.typeKey === chartType?.typeKey}
                onClick={() => {
                  handleChartTypeClick(item);
                  setDrawerOpen(false);
                }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: '1.4rem',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );

  return (
    <>
      {/* RUN PARAMETERS START   */}
      <Accordion
        defaultExpanded
        expanded={true} // this is so it should always be expanded
        onChange={handleChange('dynamicInputs')}
      >
        <AccordionSummary
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{ fontSize: '20px', fontWeight: 'bold' }}
          className={classes.accordianSummary}
        >
          Dynamic Inputs
        </AccordionSummary>
        <AccordionDetails sx={{
          padding: '0px 0px 0px 5px',
        }}>
          <Grid container>
            <Grid item xs={12}>
              {isMobile
                ? (
                  <MobileHydraulicsRunInput
                    data={properties}
                    columns={columns(renderValueCell, properties, hydraulicsLanding?.runType, hydraulicParameterData, renderModelCell)}
                    updateData={updateData}
                    loading={hydraulicsLanding?.hydraulicsRunLoading}
                  />
                  )
                : (
                  <Datagrid
                    apiRef={apiRef}
                    data={properties}
                    loading={hydraulicsLanding?.hydraulicsRunLoading}
                    sx={{
                      marginLeft: '-6px',
                      '.MuiDataGrid-cell': {
                        fontSize: '1rem',
                        fontWeight: 900,
                        pointerEvents: hydraulicsLanding?.hydraulicsRunLoading ? 'none' : 'auto',
                        backgroundColor: hydraulicsLanding?.hydraulicsRunLoading ? '#f5f5f5' : 'inherit'
                      },
                      '.MuiDataGrid-columnHeader': {
                        fontSize: '1.2rem',
                        fontWeight: '900 !important'
                      },
                      '& .MuiDataGrid-footerContainer': {
                        display: 'none',
                      },
                    }}
                    columns={columns(renderValueCell, properties, hydraulicsLanding?.runType, hydraulicParameterData, renderModelCell)}
                    onRowClick={(params, evt) => {
                      evt.preventDefault();
                      evt.stopPropagation();
                    }}
                    disableStickyHeader={false}
                    autoHeight={false}
                    autoheight
                    height={'150px'}
                    isRowSelectable={() => !hydraulicsLanding?.hydraulicsRunLoading}
                    getRowClassName={(params) =>
                      hydraulicsLanding?.hydraulicsRunLoading ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')}
                    pageSize={100}
                    editMode="cell"
                    toolbar={{
                      options: {
                        columns: false,
                        filters: false,
                        density: false,
                        export: false
                      },
                    }}
                  />
                  )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* RUN PARAMETERS END */}
      <Box className={classes.fullWidthSeparator}></Box>
      <Box className={classes.featuredWrapperListParam}>
        {isMobile
          ? renderMobileRunTypeSelector()
          : (
              <Grid className={classes.chartGrid}>
                <Grid
                  container
                  className={classes.chartTypesSelect}
                  id='chart-types-select'
                  justifyContent='center'
                  alignItems='center'
                >
                  {Object.values(HYDRAULICS_RUN_TYPES).map((item, index, arr) => {
                    return (
                      <Box key={item.typeKey} display="flex" alignItems="center">
                        <Box
                          sx={{
                            p: '0 20px',
                            cursor: 'pointer',
                            opacity: hydraulicsLanding?.hydraulicsRunLoading ? 0.5 : 1,
                            pointerEvents: hydraulicsLanding?.hydraulicsRunLoading ? 'none' : 'auto',
                          }}
                          onClick={() => !hydraulicsLanding?.hydraulicsRunLoading && handleChartTypeClick(item)}
                        >
                          <Typography
                            variant='h5'
                            className={
                              item.typeKey === chartType.typeKey
                                ? classes.active
                                : classes.inactive
                            }
                          >
                            {item.name}
                          </Typography>
                        </Box>

                        {index < arr.length - 1 && (
                          <Divider orientation="vertical" flexItem />
                        )}
                      </Box>
                    );
                  })}
                </Grid>
              </Grid>
            )
        }
        {!isMobile && (
          <Grid>
            {hydraulicsLanding?.hydraulicsRunLoading
              ? (
                <Grid display='flex' justifyContent='center' alignItems='center' mr={1}>
                  <Typography fontSize={isMobile ? '14px' : '20px'} mr={1} fontWeight='bold'>Waiting for results..</Typography>
                  <CircularProgress size='20px' />
                </Grid>
                )
              : renderSubmitButton()
            }
          </Grid>
        )}
      </Box>
      {/* WELLBORE START */}
      <Accordion className={classes.accordianBorderDisplay} defaultExpanded hidden>
        <AccordionSummary className={classes.accordianSummary} expandIcon={<GridExpandMoreIcon />}>
          Wellbore
        </AccordionSummary>
        <AccordionDetails sx={{
          padding: '0px 0px 0px 5px',
        }}>
          <Grid container>
            <Grid item xs={12}>
              <WellboreRunInputs
                checkWellbore={checkWellbore}
                properties={properties}
                setProperties={setProperties}
                anchorPoint={anchorPoint}
                targetECD={targetECD}
                setAnchorPoint={setAnchorPoint}
                setTargetECD={setTargetECD}
                setTargetData={setTargetData}
                setWellbore={setWellbore}
                wellbore={wellbore}
                setParameterDirty={setProperties}
                proxyLoader={hydraulicsLanding?.hydraulicsRunLoading}
                hydraulicParameterData={hydraulicParameterData}
                setCheckWellbore={setCheckWellbore}
                data={hydraulicsLanding.data}
                featuredWell={featuredWellRun}
                runType={hydraulicsLanding.runType}
                hydraulicsRunUnits={hydraulicsRunUnits}
                targetData={targetData}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* WELLBORE END */}
      {/* CUTTINGS START */}
      {Number(properties?.[0]?.rop) > 0 && (
        <Accordion className={classes.accordianBorderDisplay} defaultExpanded hidden>
          <AccordionSummary className={classes.accordianSummary}
            expandIcon={<GridExpandMoreIcon />}>
            Cuttings
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px 0px 0px 5px' }}>
            <Grid container>
              <Grid item xs={12}>
                <CuttingRunInputs
                  data={cuttingData}
                  setCuttingData={setCuttingData}
                  setUpdateCuttingData={setUpdateCuttingData}
                  invalidCuttingRows={invalidCuttingRows}
                  setParameterDirty={setProperties}
                  loading={hydraulicsLanding?.hydraulicsRunLoading}
                  cuttingRunUnits={hydraulicsLanding?.units?.cuttings}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
      {/* CUTTINGS END */}
      {/* Stripping Inputs START   */}
      {hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.STRIPPING?.typeKey && (
        <Accordion className={classes.accordianBorderDisplay} defaultExpanded hidden>
          <AccordionSummary className={classes.accordianSummary}
            expandIcon={<GridExpandMoreIcon />}>
            Stripping
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px 0px 0px 5px' }}>
            <Grid container>
              <Grid item xs={12}>
                <StrippingInputs
                  hydraulicsLanding={hydraulicsLanding}
                  featuredWellRun={featuredWellRun}
                  proxyLoader={hydraulicsLanding?.hydraulicsRunLoading}
                  strippingData={strippingData}
                  setStrippingData={setStrippingData}
                  setParameterDirty={setProperties}
                  hydraulicsRunUnits={hydraulicsRunUnits}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
      {/* Stripping Inputs END */}

      {/* Displacement Inputs START   */}
      {hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.DISPLACEMENT?.typeKey && (
        <Accordion className={classes.accordianBorderDisplay} defaultExpanded>
          <AccordionSummary className={classes.accordianSummary}
            expandIcon={<GridExpandMoreIcon />}>
            Displacement
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px 0px 0px 5px' }}>
            <Grid container>
              <Grid item xs={12}>
                <DisplacementInputs
                  hydraulicsLanding={hydraulicsLanding}
                  proxyLoader={hydraulicsLanding?.hydraulicsRunLoading}
                  displacementData={displacementData}
                  setDisplacementData={setDisplacementData}
                  setParameterDirty={setProperties}
                  hydraulicsRunUnits={hydraulicsRunUnits}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ROLLOVER START */}
      {hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.ROLLOVER?.typeKey && (
        <Accordion className={classes.accordianBorderDisplay} defaultExpanded>
          <AccordionSummary className={classes.accordianSummary}
            expandIcon={<GridExpandMoreIcon />} sx={{ display: 'none' }}>
            {isMobile ? (
              <Grid
                container
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid
                  item
                  xs
                  container
                  direction="column"
                  alignItems="flex-start"
                >
                  <Typography variant="h6" fontWeight={600} align="left">
                    Rollover
                  </Typography>

                  {calculatedFlowRate && (
                    <Typography
                      variant="body2"
                      sx={{ color: COLORS.LIGHT_GREY, fontWeight: 500 }}
                      align="left"
                    >
                      {`Calculated Flow Rate: ${calculatedFlowRate}`}
                    </Typography>
                  )}
                </Grid>

                {/* <Grid item>
                  <IconButton
                    variant="solid"
                    sx={{ color: mudPumpData?.length ? 'green' : 'default' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMudPumps(true);
                    }}
                    onFocus={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <HeatPump />
                  </IconButton>
                </Grid> */}
              </Grid>
            ) : (
              <Grid
                container
                width={'985px'}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" fontWeight="600">
                  Rollover
                  {calculatedFlowRate && (
                    <span style={{ color: COLORS.LIGHT_GREY, fontWeight: 500 }}>
                      {` (Calculated Flow Rate: ${calculatedFlowRate})`}
                    </span>
                  )}
                </Typography>

                {/* <IconButton
                  variant="solid"
                  sx={{ color: mudPumpData?.length ? 'green' : 'default' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMudPumps(true);
                  }}
                  onFocus={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <HeatPump />
                </IconButton> */}
              </Grid>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px 0px 0px 5px' }}>
            <Grid container>
              <Grid item xs={12}>
                <RolloverRunInputs
                  rolloverData={rolloverData}
                  setRolloverData={setRolloverData}
                  wellboreData={properties[0]}
                  hydraulicsLanding={hydraulicsLanding}
                  mudPumpData={mudPumpData}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
      {/* PARAMETRIC INPUTS START */}
      {hydraulicsLanding.runType === HYDRAULICS_RUN_TYPES?.PARAMETRIC_ANALYSIS?.typeKey && (
        <Accordion className={classes.accordianBorderDisplay} defaultExpanded hidden>
          <AccordionSummary className={classes.accordianSummary}
            expandIcon={<GridExpandMoreIcon />}>
            Parametric Analysis
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '0px 0px 0px 5px' }}>
            <Grid container>
              <Grid item xs={12}>
                <ParametricAnalysisRunInputs
                  hydraulicsParameter={hydraulicsLanding}
                  proxyLoader={hydraulicsLanding.hydraulicsRunLoading}
                  setParameterDirty={setProperties}
                  setHydraulicsParameterData={setHydraulicsParameterData}
                  parametricAnalysisData={parametricAnalysisData}
                  setParametricAnalysisData={setParametricAnalysisData}
                  saveHydraulicDetails={() => {
                    const wellBoreData = properties?.[0];
                    if (wellBoreData.well_init.pipe.length > 0 || wellBoreData.well_init.annular.length) {
                      const updatedData = (properties || []).map(item => ({
                        ...item,
                        isDirty: true,
                        well_init: {
                          pipe: [],
                          annular: []
                        },
                      }));

                      setProperties(updatedData);
                      setWellbore([]);
                    }
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
      {/* PARAMETRIC INPUTS END */}

      {
        showMudPumps
          ? <MudPumpRunInputsModel
            setParameterDirty={setProperties}
            mudPumpData={mudPumpData}
            setMudPumpData={setMudPumpData}
            isOpen={showMudPumps}
            onCloseClick={() => {
              setShowMudPumps(false);
            }}
          />
          : null
      }
    </>
  );
}

FeaturedWellRunInputs.propTypes = {
  hydraulicsLanding: PropTypes.object,
  featuredWellRun: PropTypes.object,
  chartType: PropTypes.any,
  handleChartTypeClick: PropTypes.any,
  handleChange: PropTypes.func
};

export default FeaturedWellRunInputs;
